import { onDestroy } from 'svelte';
import type { Readable } from 'svelte/store';

export interface StoreValue<T> {
  current: T;
}

/**
 * Subscribe to a readable store and expose its `current` value.
 *
 * If you need to access a store outside of a reactive environment
 * (i.e. outside a Svelte component), this gives you programmatic,
 * non-reactive access to the store's value.
 *
 * @param source The source store
 *
 * @example
 * ```ts
 * const myCoolStore = createMyCoolStore()
 * const myCoolStoreValue = useStoreValue(myCoolStore)
 *
 * console.log('Current value:', myCoolStoreValue.current)
 * ```
 */
export const useStoreValue = <T>(source: Readable<T>): StoreValue<T> => {
  let currentValue: T;

  const unsubscribe = source.subscribe(
    (nextValue) => (currentValue = nextValue)
  );

  onDestroy(() => {
    unsubscribe();
  });

  return {
    get current() {
      return currentValue;
    },
  };
};
